<template>
  <div class="paperwork-container" v-if="showPage">
    <OnboardingMenu class="menu" :logo="logoUrl" :languages="languageOptions" :menus="menuOptions"></OnboardingMenu>
    <div class="banner">
      <img :src="bannerUrl" alt="">
    </div>
    <onboarding-message class="message" :messages="messages"></onboarding-message>
    <div class="title">{{ currentStep === 0 ? $t('onboarding.nestle.newHirePaperwork') : getText(steps[currentStep - 1]['title']) }}</div>
    <div class="subtitle">{{ employeeName }}</div>
    <onboarding-divider />
    <ul v-if="currentStep === 0" class="step-zero-div">
        <li>{{ $t('onboarding.nestle.paperworkStepZeroText1') }}</li>
        <li>{{ $t('onboarding.nestle.paperworkStepZeroText2') }}</li>
        <li>{{ $t('onboarding.nestle.paperworkStepZeroText3') }}</li>
    </ul>
    <onboarding-step class="steps-component" v-if="currentStep > 0" :steps="steps" :currentStep="currentStep" :progressIcons="stepsProgressIcons"></onboarding-step>
    <div class="step-notice main-color-style common-font custom-color" v-if="currentStep > 0 && steps[currentStep - 1].notice">{{ getText(steps[currentStep - 1].notice) }}</div>
    <div class="input-form-div">
      <div v-if="!stepQuestions[`step${currentStep}`]" class="empty-div"></div>
      <div v-for="(question, index) in stepQuestions[`step${currentStep}`]" v-show="showQuestion(stepQuestions[`step${currentStep}`], question)" :key="`step${currentStep}${index}`">
        <onboarding-input :ref="`question${question['id']}`" v-if="question['type'] === 'TEXT'"
          :label="getText(question['title'])"
          :placeholder="getText(question['placeholder'])"
          v-model="question['value']"
          :regex="getRegex(question).regex"
          :tooltip="getRegex(question).tip"
          :isRequired="getRequired(question)"
          :notice="getText(question['notice'])"
          :isEditable="notEditableIds.includes(question['id']) ? 'false': ''" />
        <onboarding-dropdown :ref="`question${question['id']}`" v-if="question['type'] === 'SINGLE'"
          :label="getText(question['title'])"
          :options="getOptions(question['options'])"
          :placeholder="getText(question['placeholder'])"
          :isRequired="getRequired(question)"
          v-model="question['value']"
          :notice="getText(question['notice'])"
          :isEditable="notEditableIds.includes(question['id']) ? 'false': ''"
          @change="onDropdownChange($event, question)"></onboarding-dropdown>
        <onboarding-datetime-picker :ref="`question${question['id']}`" v-if="question['type'] === 'DATE'"
          :label="getText(question['title'])"
          :placeholder="getText(question['placeholder'])"
          :isRequired="getRequired(question)"
          v-model="question['value']"
          :isEditable="notEditableIds.includes(question['id']) ? 'false': ''" ></onboarding-datetime-picker>
        <onboarding-checkbox-option :ref="`question${question['id']}`" v-if="question['type'] === 'LINK'" align="left"
          :text="getText(question['title'])" v-model="question['value']" :isRequired="getRequired(question)"
          :link="question['options'][0] && getText(question['options'][0]['text'])"></onboarding-checkbox-option>
        <onboarding-input :ref="`question${question['id']}`" v-if="question['type'] === 'ATTACHMENT'"
          type="file"
          :fileName="question['name']"
          v-model="question['value']"
          :isRequired="getRequired(question)"
          :label="getText(question['title'])"
          :isEditable="notEditableIds.includes(question['id']) ? 'false': ''"
          :upload="question['uploadFunc']"
          :questionId="question['id']"
          :fileType="['gif', 'png', 'bmp', 'jpeg', 'pjpeg', 'tiff', 'x-png', 'jpg']"
          @input="handleFileChange($event, question)"></onboarding-input>
      </div>
    </div>
    <div class="next-button">
      <onboarding-button v-if="currentStep > 0" @click.native="toLastStep" :text="$t('onboarding.nestle.back')" type='light' width="large"></onboarding-button>
      <onboarding-button @click.native="saveCurrentStep" :text="$t('onboarding.nestle.cancel')" type='light' width="large"></onboarding-button>
      <onboarding-button @click.native="toNextStep" :text="(currentStep > 0 && currentStep === steps.length) ? $t('onboarding.nestle.finish') : $t('onboarding.nestle.next')" type='normal' width="large"></onboarding-button>
    </div>
  </div>
</template>
<script>
import OnboardingInput from '@/components/onboarding/form/Input'
import OnboardingDropdown from '@/components/onboarding/form/Dropdown'
import OnboardingDivider from '@/components/onboarding/form/Divider'
import OnboardingDatetimePicker from '@/components/onboarding/form/DatetimePicker'
import OnboardingStep from '@/components/onboarding/form/Step'
import OnboardingCheckboxOption from '@/components/onboarding/form/CheckboxOption'
import OnboardingButton from '@/components/onboarding/form/Button'
import OnboardingMenu from '@/components/onboarding/form/Menu'
import OnboardingMessage from '@/components/onboarding/form/Message'
import { onboardingMixin } from '@/components/onboarding/form/mixin.js'
import { mapActions, mapGetters } from 'vuex'
import _ from 'lodash'
import * as qiniu from 'qiniu-js'
import time from '@/utils/time'
import moment from 'moment'
import { deviceType } from '@/utils/common'

const nationalIdTypeQuestionId = [26, 52, 53]
const needSortOptionsQuestionId = [9, 11, 12, 25]

export default {
  mixins: [onboardingMixin],
  components: {
    OnboardingInput, OnboardingDropdown, OnboardingDivider, OnboardingDatetimePicker, OnboardingStep, OnboardingCheckboxOption, OnboardingButton, OnboardingMenu, OnboardingMessage
  },
  data () {
    return {
      bannerUrl: require('@/assets/image/onboarding/common/banner.png'),
      employeeName: '',
      showPage: false,
      account: {},
      notEditableIds: [],
      stepsProgressIcons: {
        finishedIcon: { color: 'gray', address: 'iconduigou' },
        currentIcon: { color: '#00539F', address: 'icondot' },
        unfinishedIcon: { color: 'gray', address: 'iconshixinxiaoyuanquan' }
      },
      stepProgressIcons: [
        require('@/assets/image/onboarding/common/step-finished.png'),
        require('@/assets/image/onboarding/common/step-current.png'),
        require('@/assets/image/onboarding/common/step-unfinished.png')
      ],
      steps: [],
      stepQuestions: {},
      questionPidMap: {},
      dynamicQuestionMap: {
        _allChildren: []
      },
      fileUploadQuestion: {}
    }
  },
  computed: {
    questionnaireId () {
      return this.$route.params.questionnaireId
    },
    currentStep () {
      return this.$route.query.step ? Number(this.$route.query.step) : 0
    },
    invitationId () {
      return this.$route.params.invitationId
    }
  },
  async created () {
    const isMobile = deviceType.isMobile
    this.bannerUrl = require(isMobile ? '@/assets/image/onboarding/common/mobile-banner.jpg' : '@/assets/image/onboarding/common/banner.png')
    this.employeeName = this.getCurrentAccount().full_name
    this.$eventBus.$emit('showLoading', true)
    await this.getSteps(this.questionnaireId)
    if (this.currentStep > 0 && this.currentStep - 1 < this.steps.length) {
      await this.getSingleStep(this.questionnaireId, (this.currentStep), this.invitationId)
    }
    this.$eventBus.$emit('showLoading', false)
    this.showPage = true
  },
  methods: {
    ...mapActions('onboarding', ['getOnboardingSteps', 'getStepQuestions', 'answerQuestions']),
    ...mapGetters('account', ['getCurrentAccount']),
    getRegex (question) {
      let regex = ''
      let tip = ''
      const questions = this.stepQuestions[`step${this.currentStep}`]
      const nationalIDQuestion = questions.find(item => nationalIdTypeQuestionId.includes(item.id) && this.showQuestion(questions, item))
      if (nationalIDQuestion && question.id === 27) {
        switch (parseInt(nationalIDQuestion.value)) {
          case 879:
          case 2111:
          case 2114:
            regex = '^[\\s\\S]{0,10}$'
            tip = this.$t('onboarding.nestle.error.nationalIDRegex', { nationID: 'Taiwan ID', format: 'XXXXXXXXXX' })
            break
          case 880:
          case 2112:
          case 2115:
            regex = '^[\\s\\S]{0,11}$'
            tip = this.$t('onboarding.nestle.error.nationalIDRegex', { nationID: 'HKID - HKID Number', format: 'XXXXXXXXXXX' })
            break
          default:
            regex = '^[0-9]{17}[a-zA-Z0-9]{1}$'
            tip = this.$t('onboarding.nestle.error.nationalIDRegex', { nationID: 'Personal ID Number', format: 'NNNNNNYYYYMMDDNNNX' })
        }
      }
      return { regex: regex, tip: tip }
    },
    getRequired (question) {
      const questionId = question.id
      const isRequired = question.is_required
      if (isRequired && questionId === 8) {
        return this.$t('onboarding.nestle.error.birthIsNeeded')
      }
      if (isRequired && questionId === 29) {
        return this.$t('onboarding.nestle.error.nationalIDIssueDateIsNeeded')
      }
      if (isRequired && questionId === 30) {
        return this.$t('onboarding.nestle.error.nationalIDExpireDateIsNeeded')
      }
      if (isRequired && questionId === 35) {
        return this.$t('onboarding.nestle.error.mustSelected')
      }
      if (isRequired && questionId === 36) {
        return this.$t('onboarding.nestle.error.mustSelected')
      }
      return isRequired
    },
    async uploadFileForQuestion (questionId, uploader) {
      if (!questionId) {
        this.showMessage(this.$t('onboarding.nestle.error.uploadError'), 'error', 3)
        return
      }
      this.$store.dispatch('common/getQiniuUploadToken').then((resp) => {
        const token = resp.token
        const selectedFiles = this.$refs[`question${questionId}`][0].$refs['file-input'].files
        if (selectedFiles.length === 1) {
          const selectedFile = selectedFiles[0]
          const fileName = selectedFile.name
          const observable = qiniu.upload(selectedFile, undefined, token, {}, { region: qiniu.region.z0 })
          uploader.inUploadProgress = true
          observable.subscribe(
            (resp) => {
              // show progress
              this.fileUploadQuestion[questionId] = 'uploading'
            }, (resp) => {
              resp && resp.message && this.showMessage(this.$t('onboarding.nestle.error.uploadError'), 'error', 3)
              this.fileUploadQuestion[questionId] = 'fail'
              uploader.inUploadProgress = false
            }, (resp) => {
              const qiniuFileKey = resp && resp.key
              const currentStepQuestions = this.stepQuestions[`step${this.currentStep}`]
              const question = currentStepQuestions.find(item => item.id === questionId)
              const questionIndex = currentStepQuestions.indexOf(question)
              if (question && questionIndex > -1) {
                question.value = `${qiniuFileKey} ${fileName}`
                question.fileName = fileName
                question.name = fileName
                this.stepQuestions[`step${this.currentStep}`][questionIndex] = question
                this.showMessage(this.$t('onboarding.nestle.error.uploadSuccess'), 'success', 3)
                this.$nextTick(() => {
                  this.fileUploadQuestion[questionId] = 'finished'
                })
              }
              uploader.inUploadProgress = false
            }
          )
        }
      })
      // subscription.unsubscribe() // 上传取消
    },
    async getSingleStep (questionnaireId, stepNum, invitationId) {
      const stepId = this.steps[stepNum - 1].id
      const singleStepResp = await this.getStepQuestions({ questionnaireId: questionnaireId, stepId: stepId, invitationId: invitationId })
      if (singleStepResp && singleStepResp.questions) {
        const questions = singleStepResp.questions
        for (let index = 0; index < questions.length; index++) {
          if (questions[index].type === 'LINK' && !questions[index].value) {
            questions[index].value = '0'
          }
          if (questions[index].type === 'ATTACHMENT') {
            questions[index].file = ''
            const value = questions[index].value
            if (value && value.length > 0 && value.indexOf(' ') > -1) {
              const splitCharIndex = value.indexOf(' ')
              questions[index].name = value.slice(splitCharIndex + 1, value.length)
            }
            questions[index].uploadFunc = this.uploadFileForQuestion
          }
          if (questions[index].type === 'SINGLE') {
            const question = questions[index]
            const pid = question.pid
            if (pid) {
              let children = this.questionPidMap[pid]
              if (!children) {
                children = []
                this.questionPidMap[pid] = children
              }
              children.push(question)
            }
            if (question.has_dynamic_children) {
              const childrenQuestion = {}
              _.forEach(question.options, option => {
                childrenQuestion[option.id] = option.dynamic_children_question_ids.split(',').map(item => parseInt(item))
                _.forEach(childrenQuestion[option.id], item => {
                  if (!this.dynamicQuestionMap._allChildren.includes(item)) {
                    this.dynamicQuestionMap._allChildren.push(item)
                  }
                })
              })
              this.dynamicQuestionMap[question.id] = childrenQuestion
            }
            if (needSortOptionsQuestionId.includes(question.id)) {
              question.options = _.sortBy(question.options, item => item.text.en)
            }
          }
        }
        this.stepQuestions[`step${stepNum}`] = this.sortSteps(questions)
      }
    },
    async getSteps (questionnaireId) {
      const invitationInfo = this.$parent.invitationInfo
      if (invitationInfo && invitationInfo.is_finished) {
        this.$router.push({ path: `/onboarding/${this.invitationId}/nestle/landing` })
        return
      }
      const resp = await this.getOnboardingSteps(questionnaireId)
      this.steps = (resp && resp.steps && this.sortSteps(resp.steps)) || []
    },
    validateForm () {
      const currentQuestionIds = this.getStepQuestionIds(this.currentStep)
      let result = true
      let nationalIDIssueDate = 0
      let nationalIDExpireDate = 0
      let birthDate = 0
      let firstName = 0
      let lastName = 0
      for (const id of currentQuestionIds) {
        let ref = this.$refs[`question${id}`]
        if (ref.length > 0) {
          ref = ref[0]
        }
        if (ref.$el.parentElement.style.display === 'none') {
          continue
        }
        if (!ref.validate()) {
          result = false
        }
        if (id === 2) {
          firstName = ref.internalValue
        }
        if (id === 4) {
          lastName = ref.internalValue
        }
        if (id === 8) {
          birthDate = ref.internalValue
        }
        if (id === 29) {
          nationalIDIssueDate = ref.internalValue
        }
        if (id === 30) {
          nationalIDExpireDate = ref.internalValue
        }
      }
      if (firstName) {
        if (!this.checkEn(firstName)) {
          result = false
          this.showMessage(this.$t('onboarding.nestle.error.nameNotEn'), 'error', 3)
        }
      }
      if (lastName) {
        if (!this.checkEn(lastName)) {
          result = false
          this.showMessage(this.$t('onboarding.nestle.error.nameNotEn'), 'error', 3)
        }
      }

      if (birthDate) {
        const now = new Date()
        // at least 16 year old
        const smallestBirthYearDate = time.getDayZeroDate(now.setFullYear(now.getFullYear() - 16).valueOf())
        if (birthDate > smallestBirthYearDate) {
          result = false
          this.showMessage(this.$t('onboarding.nestle.error.ageTooSmall'), 'error', 3)
        }
      }
      if (nationalIDIssueDate && nationalIDExpireDate) {
        if (typeof nationalIDIssueDate === 'string') {
          nationalIDIssueDate = nationalIDIssueDate.replace(/\//g, '-')
          nationalIDIssueDate = moment(nationalIDIssueDate).valueOf()
        }
        if (typeof nationalIDExpireDate === 'string') {
          nationalIDExpireDate = nationalIDExpireDate.replace(/\//g, '-')
          nationalIDExpireDate = moment(nationalIDExpireDate).valueOf()
        }
        if (nationalIDExpireDate <= nationalIDIssueDate) {
          result = false
          this.showMessage(this.$t('onboarding.nestle.error.nationalIDExpireLargeThanIssue'), 'error', 3)
        } else if (nationalIDExpireDate < moment().valueOf()) {
          result = false
          this.showMessage(this.$t('onboarding.nestle.error.nationalIDExpireLargeThanNow'), 'error', 3)
        }
      }
      return result
    },
    checkEn (value) {
      return /^[a-zA-Z0-9 ]+$/g.test(value)
    },
    getStepQuestionIds (stepId) {
      const questions = this.stepQuestions[`step${stepId}`]
      if (!questions) {
        return []
      }
      return questions.map((question) => question.id)
    },
    async submitAnswers (isLastPage, willClose = false) {
      const answers = []
      const currentQuestions = this.stepQuestions[`step${(this.currentStep)}`]
      for (const question of currentQuestions) {
        const id = question.id
        let ref = this.$refs[`question${id}`]
        if (ref.length > 0) {
          ref = ref[0]
        }
        if (ref.$el.parentElement.style.display === 'none') {
          continue
        }
        let value = question.value
        const uploadStatus = this.fileUploadQuestion[id]
        if (uploadStatus === 'changed') {
          value = ref.value
        }
        answers.push({ question_id: id, value: value })
      }
      const params = {
        questionnaireId: this.questionnaireId,
        answers: answers,
        isLastPage: isLastPage
      }
      const resp = await this.answerQuestions(params)
      if (resp && (willClose || isLastPage)) {
        this.$router.push({ path: `/onboarding/${this.invitationId}/nestle/landing` })
      }
      return true
    },
    toLastStep () {
      if (this.currentStep === 0) {
        return
      }
      this.fileUploadQuestion = {}
      this.$router.push(`${this.$route.path}?step=${this.currentStep - 1}`)
    },
    saveCurrentStep () {
      this.toVuePage({ path: `/onboarding/${this.invitationId}/nestle/landing` })
    },
    async toNextStep () {
      for (const questionId in this.fileUploadQuestion) {
        const uploadStatus = this.fileUploadQuestion[questionId]
        if (uploadStatus === 'changed') {
          if (!confirm(this.$t('onboarding.nestle.fileChangedButNoUpload'))) {
            return
          }
        }
      }
      if (!this.validateForm()) {
        return
      }
      if (this.steps.length === 0) {
        this.showMessage(this.$t('onboarding.nestle.error.noSteps'), 'error', 3)
        return
      }
      this.$eventBus.$emit('showLoading', true)
      try {
        if (this.currentStep > 0) {
          const result = await this.submitAnswers(this.currentStep === this.steps.length)
          if (result === false) {
            this.showMessage(this.$t('onboarding.nestle.error.tryAgain'), 'error', 3)
            return
          }
        }
        if (this.currentStep < this.steps.length) {
          this.$router.push(`${this.$route.path}?step=${this.currentStep + 1}`)
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    resetRegex (selectedValue, question) {
      const regexValueMap = {
        tw: '^[\\s\\S]{0,10}$',
        hk: '^[\\s\\S]{0,11}$',
        default: '^[0-9]{17}[a-zA-Z0-9]{1}$'
      }
      const toolTipMessageMap = {
        tw: this.$t('onboarding.nestle.error.nationalIDRegex', { nationID: 'Taiwan ID', format: 'XXXXXXXXXX' }),
        hk: this.$t('onboarding.nestle.error.nationalIDRegex', { nationID: 'HKID - HKID Number', format: 'XXXXXXXXXXX' }),
        default: this.$t('onboarding.nestle.error.nationalIDRegex', { nationID: 'Personal ID Number', format: 'NNNNNNYYYYMMDDNNNX' })
      }
      const targetId = 27
      let mapKey = null
      switch (selectedValue) {
        case 879:
        case 2111:
        case 2114:
          mapKey = 'tw'
          break
        case 880:
        case 2112:
        case 2115:
          mapKey = 'hk'
          break
        case 881:
        case 2113:
        case 2116:
          mapKey = 'default'
          break
      }
      this.$refs[`question${targetId}`][0].regexValue = regexValueMap[mapKey]
      this.$refs[`question${targetId}`][0].toolTipMessage = toolTipMessageMap[mapKey]
      this.$refs[`question${targetId}`][0].validate()
    },
    onDropdownChange (selectedValue, question) {
      if (nationalIdTypeQuestionId.includes(question.id)) {
        this.resetRegex(selectedValue, question)
      }
      const id = question.id
      const children = this.questionPidMap[id]
      if (children && children.length > 0) {
        const selectOption = question.options.find(item => item.id === selectedValue)
        const originPid = selectOption.origin_id
        children.forEach(child => {
          const childDropdown = this.$refs[`question${child.id}`][0]
          let options = child.options.filter(item => item.origin_pid === originPid)
          options = this.getOptions(options)
          options.forEach(item => { item.isShow = true })
          childDropdown.displayText = null
          childDropdown.inputValue = null
          childDropdown.internalValue = null
          childDropdown.showItem = false
          childDropdown.searchedOptions = options
          if (options.length === 1 && [45, 46, 49].includes(child.id)) {
            const optionItem = options[0]
            childDropdown.internalValue = optionItem.id
            childDropdown.internalErrorMessage = ''
          }
        })
      }
      if (question.has_dynamic_children) {
        const childQuestions = this.dynamicQuestionMap[id][selectedValue]
        _.forEach(this.dynamicQuestionMap._allChildren, questionId => {
          if (nationalIdTypeQuestionId.includes(questionId)) {
            const questions = this.stepQuestions[`step${this.currentStep}`]
            const nataionalIdTypeQuestion = _.find(questions, item => nationalIdTypeQuestionId.includes(item.id) && this.showQuestion(questions, item))
            if (nataionalIdTypeQuestion) this.resetRegex(parseInt(nataionalIdTypeQuestion.value), nataionalIdTypeQuestion)
          }
          let questionRef = this.$refs[`question${questionId}`]
          if (questionRef && questionRef.length > 0) {
            questionRef = questionRef[0]
            const style = questionRef.$el.parentElement.style
            if (childQuestions.indexOf(questionId) > -1) {
              style.display = null
            } else {
              style.display = 'none'
            }
          }
        })
      }
    },
    showQuestion (allquestion, question) {
      if (this.dynamicQuestionMap._allChildren.includes(question.id)) {
        for (const parentId in this.dynamicQuestionMap) {
          const parent = _.find(allquestion, { id: parseInt(parentId) })
          if (parent) {
            const childrenQuestionMap = this.dynamicQuestionMap[parentId]
            const optionId = parent.value
            const childrenQuestion = childrenQuestionMap[optionId]
            return childrenQuestion && childrenQuestion.includes(question.id)
          }
        }
        return false
      }
      return true
    },
    handleFileChange (event, question) {
      this.fileUploadQuestion[question.id] = 'changed'
    }
  }
}
</script>
<style lang="scss" scoped>
@import '@/assets/scss/onboarding-common.scss';
  .paperwork-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    .banner {
        img {
          background-size: cover;
          width: 100%;
          height: auto;
        }
    }
    .message {
      position: fixed;
      top: 50px;
    }
    .title {
      font-weight: bolder;
      margin-top: 2rem;
      margin-bottom: 1.2rem;
      text-align: center;
    }
    .subtitle {
      text-align: center;
      color: gray;
    }
    .divider {
      margin-left: auto;
      margin-right: auto;
      margin-bottom: 2.5rem;
      float: none;
    }
    .empty-div {
      height: 50px;
    }
    .step-zero-div {
      width: 75%;
      color: gray;

      li {
        margin: 2rem 0;
        list-style-type: disc;
        font-size: smaller !important;
      }
    }
    .step-notice {
      width: 90%;
      margin: 2rem auto;
      text-align: left;
      font-weight: bolder;
    }
    .steps-component {
      margin-bottom: 20px;
    }
    .input-form-div {
      width: 100%;
      padding: 0 5%;
    }
    .next-button {
      width: 100%;
      margin: 4rem 2.5rem 4rem 0;
      padding-bottom: 100px;
      display: flex;
      justify-content: space-around;
      align-items: center;
    }
  }
/* 小型设备（平板电脑，768px 起） */
@media (min-width: 768px) {
    .next-button {
      width: 40% !important;
    }
    .steps-component {
      margin-bottom: 100px !important;
    }
    .step-zero-div {
      width: 40% !important;
      li {
        list-style-type: disc;
        margin-top: 30px !important;
        font-size: smaller !important;
      }
    }
}
</style>
